import { formatDateToString } from '@/utils/dates';
import ConstituentField from './constants/ConstituentField';
import TemporaryField from './constants/TemporaryField';

const temporaryFields = Object.values(TemporaryField);

// Recursively trim any string values, swapping empty strings with `null`.
const trimString = (str) => (str.trim().length ? str.trim() : null);
const formatValue = (val) => {
  let formattedVal = val;
  if (typeof val === 'string') {
    formattedVal = trimString(val);
  }
  if (Array.isArray(val)) {
    formattedVal = val.map(formatValue);
  } else if (typeof val === 'object' && val !== null) {
    formattedVal = Object.entries(val).reduce(
      (obj, [key, value]) => ({
        ...obj,
        [key]: formatValue(value),
      }),
      {},
    );
  }
  return formattedVal;
};

const mapDataForApi = (formData) => {
  const formattedData = Object.entries(formData).reduce(
    (obj, [key, val]) =>
      temporaryFields.includes(key) // strip temporary fields
        ? obj
        : { ...obj, [key]: formatValue(val) },
    {},
  );

  // Map attachment data. Attachments are read as an array of objects, but
  // written as an array of id strings, which we are keeping in a temporary
  // field.
  formattedData[ConstituentField.ATTACHMENTS] =
    formData[TemporaryField.UPLOAD_IDS];

  // Strip any empty objects from our array fields.
  const fieldArrays = [
    [
      ConstituentField.ADDRESSES,
      ({ city, gmapsPlaceId, line1, line2, postalCode, state, unit }) =>
        city || gmapsPlaceId || line1 || line2 || postalCode || state || unit,
    ],
    [ConstituentField.EMAILS, ({ email }) => email],
    [ConstituentField.PHONES, ({ number }) => number],
  ];

  fieldArrays.forEach(([name, filter]) => {
    if (!formattedData[name]) return;
    formattedData[name] = formattedData[name].filter(filter);
    // If no remaining items are marked as "primary", then mark the first one
    if (
      formattedData[name].length &&
      !formattedData[name].some(({ primary }) => primary)
    ) {
      formattedData[name][0].primary = true;
    }
  });

  // Re-map custom field data. This is the format it reads and writes as, but is
  // changed into an object for form access.
  const customFieldsData = formData[ConstituentField.CUSTOM_FIELDS];

  const getValueFromCustomField = (value) => {
    if (value instanceof Date) return formatDateToString(value);
    if (typeof value === 'object' && 'value' in value) return [value.value];
    if (Array.isArray(value) && value.length > 0)
      return value.map((val) => val?.value || val);
    return value;
  };

  formattedData[ConstituentField.CUSTOM_FIELDS] =
    customFieldsData &&
    Object.keys(customFieldsData)
      .map((fieldId) =>
        customFieldsData[fieldId]
          ? {
              custom_field_id: fieldId,
              value: getValueFromCustomField(customFieldsData[fieldId]),
            }
          : null,
      )
      .filter(Boolean);

  return formattedData;
};

export default mapDataForApi;
